/**
 * One recorded step of one ROI's contour history, as it can be recognised
 * again later. Its position in the stack is deliberately not part of it:
 * the stack evicts intermediate steps, which renumbers everything after
 * them.
 */
export interface ContourEditStep {
    /** The operation label the step was recorded under. */
    readonly operation: string;
    readonly recordedAtMs: number;
}

/**
 * The geometry an ROI's contour shows, as its history names it: a recorded
 * step, or 'initial' for the state before this session's first edit. The
 * history records that state as its initial step only once the first edit
 * lands, so before then it has no recorded time.
 */
export type ContourStepRef = ContourEditStep | 'initial';

/** A restore of an ROI's contour: the step it left and the step it restored. */
export interface ContourStepMove {
    readonly from: ContourStepRef;
    readonly to: ContourStepRef;
}

/** Whether two references name the same step of one ROI's history. */
export function isSameContourStep(
    a: ContourStepRef,
    b: ContourStepRef
): boolean {
    if (a === 'initial' || b === 'initial') {
        return a === b;
    }
    return a.operation === b.operation && a.recordedAtMs === b.recordedAtMs;
}
